import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, Button, TextField, MenuItem, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Avatar, IconButton, Tooltip, CircularProgress,
  InputAdornment, alpha, useTheme, Alert,
} from '@mui/material';
import {
  Package, Search, ExternalLink, RefreshCw, Sparkles, Zap,
} from 'lucide-react';
import axios from '../utils/axios';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './common/modal';
import { getProductImageUrl } from '../utils/helpers';
import { formatDateTime } from '../utils/formatDate';
import EmptyState from './common/EmptyState';

const SITE_URL = 'https://www.resellerbazzar.in';

const isHexId = (val) => typeof val === 'string' && /^[0-9a-fA-F]{24}$/.test(val.trim());

export default function BusinessProductsModal({ open, onClose, business }) {
  const theme = useTheme();

  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Search & Filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchProducts = useCallback(async () => {
    if (!business?._id) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`/admin/businesses/${business._id}/products`, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch || undefined,
          status: statusFilter && statusFilter !== 'all' ? statusFilter : undefined,
        },
        skipNotify: true,
      });
      setProducts(res.data?.products || []);
      setTotalProducts(res.data?.totalProducts || 0);
    } catch (err) {
      console.error('Failed to load business products:', err);
      setError(err.response?.data?.msg || 'Could not load connected products');
    } finally {
      setLoading(false);
    }
  }, [business?._id, page, rowsPerPage, debouncedSearch, statusFilter]);

  useEffect(() => {
    if (open) {
      fetchProducts();
    } else {
      setProducts([]);
      setTotalProducts(0);
      setSearch('');
      setDebouncedSearch('');
      setStatusFilter('all');
      setPage(0);
    }
  }, [open, fetchProducts]);

  if (!business) return null;

  const storeUrl = business.slug ? `${SITE_URL}/store/${business.slug}` : null;

  return (
    <AdminDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <AdminDialogHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {business.business_name}
            </Typography>
            <Chip
              icon={<Package size={14} />}
              label={`${totalProducts} Connected Products`}
              size="small"
              color="primary"
              sx={{ fontWeight: 700 }}
            />
          </Stack>
        }
        subtitle={
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              Category: <b>{business.business_category || 'N/A'}</b>
            </Typography>
            <Typography variant="caption" color="text.disabled">•</Typography>
            <Typography variant="caption" color="text.secondary">
              Location: <b>{business.city ? `${business.city}, ${business.state || ''}` : 'India'}</b>
            </Typography>
            {business.owner && (
              <>
                <Typography variant="caption" color="text.disabled">•</Typography>
                <Typography variant="caption" color="text.secondary">
                  Owner: <b>{business.owner.first_name} {business.owner.last_name}</b>
                </Typography>
              </>
            )}
          </Stack>
        }
        icon={Package}
        onClose={onClose}
      >
        {storeUrl && (
          <Tooltip title="View Public Storefront on Reseller Bazzar">
            <Button
              size="small"
              variant="outlined"
              color="primary"
              component="a"
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<ExternalLink size={14} />}
              sx={{ fontWeight: 700, borderRadius: 2, textTransform: 'none' }}
            >
              Storefront
            </Button>
          </Tooltip>
        )}
      </AdminDialogHeader>

      <AdminDialogContent dividers sx={{ p: 0 }}>
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        {/* Filters & Search Toolbar */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            bgcolor: 'background.neutral',
          }}
        >
          <TextField
            size="small"
            placeholder="Search products by title, category, keywords..."
            value={search}
            onChange={(e) => setSearch(e.target.value.trimStart())}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} color={theme.palette.text.secondary} />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 320 }, bgcolor: 'background.paper', borderRadius: 1.5 }}
          />

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              SelectProps={{
                displayEmpty: true,
              }}
              sx={{ minWidth: 150, bgcolor: 'background.paper', borderRadius: 1.5 }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="Activated">Activated</MenuItem>
              <MenuItem value="Deactivated">Deactivated</MenuItem>
            </TextField>

            <Tooltip title="Refresh products list">
              <IconButton onClick={fetchProducts} size="small" disabled={loading} sx={{ bgcolor: 'background.paper' }}>
                <RefreshCw size={17} className={loading ? 'animate-spin' : ''} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Products Table */}
        <TableContainer sx={{ minHeight: 340, maxHeight: 540 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'background.neutral', fontWeight: 700 } }}>
                <TableCell>Product</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Added Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      Loading connected products...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : products.length > 0 ? (
                products.map((prod) => {
                  const isActivated = prod.product_status === 'Activated';
                  const prodImgUrl = getProductImageUrl(prod.product_image);
                  const publicUrl = prod.slug ? `${SITE_URL}/product/${prod.slug}` : null;

                  return (
                    <TableRow key={prod._id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 220 }}>
                          <Avatar
                            variant="rounded"
                            src={prodImgUrl}
                            sx={{ width: 44, height: 44, bgcolor: 'background.neutral', borderRadius: 1.5 }}
                          >
                            <Package size={20} color="#919EAB" />
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                              {prod.product_name || 'Untitled Product'}
                            </Typography>
                            {prod.brand_name && (
                              <Typography variant="caption" color="text.secondary" noWrap display="block">
                                Brand: {prod.brand_name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.25} alignItems="flex-start">
                          {prod.category && !isHexId(prod.category) ? (
                            <Chip
                              label={prod.category}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 11, height: 20, fontWeight: 600 }}
                            />
                          ) : prod.sub_category ? (
                            <Chip
                              label={prod.sub_category}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 11, height: 20, fontWeight: 600 }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                          {prod.sub_category && prod.category && !isHexId(prod.category) && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
                              {prod.sub_category}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          {prod.price ? `₹${Number(prod.price).toLocaleString('en-IN')}` : '—'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <Chip
                            label={prod.product_status || 'Activated'}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              borderRadius: 1,
                              fontSize: 11,
                              height: 22,
                              bgcolor: alpha(isActivated ? theme.palette.success.main : theme.palette.warning.main, 0.12),
                              color: isActivated ? 'success.dark' : 'warning.dark',
                            }}
                          />
                          {prod.isFeatured && (
                            <Tooltip title="Featured Product">
                              <Chip
                                icon={<Sparkles size={11} />}
                                label="Featured"
                                size="small"
                                color="warning"
                                sx={{ height: 20, fontSize: 10, fontWeight: 800 }}
                              />
                            </Tooltip>
                          )}
                          {prod.boosted_3_days && (
                            <Tooltip title="Boosted Listing">
                              <Chip
                                icon={<Zap size={11} />}
                                label="Boosted"
                                size="small"
                                color="secondary"
                                sx={{ height: 20, fontSize: 10, fontWeight: 800 }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                        {formatDateTime(prod.createdAt || prod.created_at)}
                      </TableCell>

                      <TableCell align="right">
                        {publicUrl && (
                          <Tooltip title="View product page on website">
                            <IconButton
                              size="small"
                              component="a"
                              href={publicUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ color: 'text.secondary' }}
                            >
                              <ExternalLink size={16} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, border: 'none' }}>
                    <EmptyState
                      icon={Package}
                      title="No products connected"
                      description={
                        search || statusFilter
                          ? 'No products match your active search or status filter.'
                          : `"${business.business_name}" does not have any products uploaded yet.`
                      }
                      actionLabel={search || statusFilter ? 'Clear Filters' : undefined}
                      onAction={
                        search || statusFilter
                          ? () => {
                              setSearch('');
                              setStatusFilter('');
                              setPage(0);
                            }
                          : undefined
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {products.length} of {totalProducts} products
          </Typography>
          <TablePagination
            component="div"
            count={totalProducts || 0}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{ border: 'none' }}
          />
        </Box>
      </AdminDialogContent>

      <AdminDialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit" sx={{ fontWeight: 700, borderRadius: 2 }}>
          Close
        </Button>
      </AdminDialogActions>
    </AdminDialog>
  );
}
